import type { NestedKeyOf } from "../../../../../common/Utils";
import { nameOf } from "../../../../../common/Utils";
import type { OrderFormData, OrderItemWoodData, OrderItemArticleData } from "../contexts/FormContexts";

/**
 * Builds a type-safe form key for wood order items.
 *
 * @param index - The index of the item in the itemsWood array
 * @param field - The field name of the wood item
 * @returns A type-safe nested key for use with Mantine form
 *
 * @example
 * ```typescript
 * buildWoodItemKey(0, 'woodTypeId')
 * // Returns: "itemsWood.0.woodTypeId"
 * ```
 */
export function buildWoodItemKey<K extends keyof OrderItemWoodData>(
  index: number,
  field: K,
): NestedKeyOf<OrderFormData> {
  return `${nameOf<OrderFormData>().itemsWood}.${index}.${String(field)}` as NestedKeyOf<OrderFormData>;
}

/**
 * Builds a type-safe form key for article order items.
 *
 * @param index - The index of the item in the itemsArticle array
 * @param field - The field name of the article item
 * @returns A type-safe nested key for use with Mantine form
 *
 * @example
 * ```typescript
 * buildArticleItemKey(0, 'sku')
 * // Returns: "itemsArticle.0.sku"
 * ```
 */
export function buildArticleItemKey<K extends keyof OrderItemArticleData>(
  index: number,
  field: K,
): NestedKeyOf<OrderFormData> {
  return `${nameOf<OrderFormData>().itemsArticle}.${index}.${String(field)}` as NestedKeyOf<OrderFormData>;
}
